import { create } from 'zustand';
import { products, type Product, type DesignTemplate } from '@/config/products';
import {
  createTextureFactory,
  type TextureFactory,
  type DesignZone,
} from '@/core/texture/textureFactory';
import { createView3D, loadGLTF, type View3D } from '@/core/three/view3d';
import {
  decodeShareHash,
  encodeShareHash,
  loadLocalDesign,
  saveLocalDesign,
  serializeDesign,
  resolveTextStyle,
  DEFAULT_SIZE,
  type Artwork,
  type ArtworkPositionId,
  type ArtworkSide,
  type GarmentSize,
  type PatternLayer,
  type PatternRegionId,
  type Personalisation,
  type SerializedDesign,
  type TextStyle,
  type TextStyleKey,
} from '@/lib/designState';
import { loadDesignBySlug, saveDesignToCloud } from '@/lib/designsApi';
import { supabaseEnabled } from '@/lib/supabase';
import { loadLead, type Lead } from '@/lib/leads';

export type WizardStep = 0 | 1 | 2;
export type PanelTab = 'colors' | 'text' | 'logo' | 'patterns' | 'size';

/** Minimum time each loading phase stays readable before the overlay can dismiss. */
export const LOADING_MIN_MS = 1000;

/** Selection id for print objects: `text:team` | `text:name` | `text:number` | artwork uuid */
export type PrintSelection = string | null;

const TEXT_ROLES = new Set(['team', 'name', 'number']);

function isTextSelection(id: string | null): id is `text:${TextStyleKey}` {
  return !!id && id.startsWith('text:') && TEXT_ROLES.has(id.slice(5));
}

/** UV-mapped print anchors on the 2000² texture (flipY=false). Measured from football-shirt.glb. */
export const ARTWORK_POSITIONS: Record<
  ArtworkPositionId,
  { label: string; side: ArtworkSide; x: number; y: number; defaultSize: number }
> = {
  // Front body UV island ≈ x 80–550, y 1250–1900 (viewer left = lower canvas X)
  'front-top-left': { label: 'Front · Top left', side: 'front', x: 220, y: 1310, defaultSize: 160 },
  'front-top-center': { label: 'Front · Top center', side: 'front', x: 338, y: 1310, defaultSize: 140 },
  'front-top-right': { label: 'Front · Top right', side: 'front', x: 462, y: 1310, defaultSize: 160 },
  'front-center': { label: 'Front · Center', side: 'front', x: 338, y: 1570, defaultSize: 220 },
  'front-bottom': { label: 'Front · Bottom', side: 'front', x: 339, y: 1850, defaultSize: 180 },
  // Back body UV island ≈ x 700–1300, y 1200–1900 (viewer left when facing back = lower canvas X)
  'back-top': { label: 'Back · Top', side: 'back', x: 1000, y: 1235, defaultSize: 160 },
  'back-middle': { label: 'Back · Middle', side: 'back', x: 1000, y: 1520, defaultSize: 200 },
  'back-bottom': { label: 'Back · Bottom', side: 'back', x: 998, y: 1820, defaultSize: 180 },
  'back-left': { label: 'Back · Left', side: 'back', x: 874, y: 1310, defaultSize: 140 },
  'back-right': { label: 'Back · Right', side: 'back', x: 1127, y: 1310, defaultSize: 140 },
  // Sleeves: L ≈ x 200–450, R ≈ x 850–1150, y 760–1030
  'sleeve-left': { label: 'Sleeve · Left', side: 'front', x: 324, y: 910, defaultSize: 120 },
  'sleeve-right': { label: 'Sleeve · Right', side: 'front', x: 982, y: 911, defaultSize: 120 },
  'sleeve-left-top': { label: 'Sleeve L · Top', side: 'front', x: 330, y: 800, defaultSize: 100 },
  'sleeve-left-bottom': { label: 'Sleeve L · Cuff', side: 'front', x: 300, y: 1015, defaultSize: 100 },
  'sleeve-right-top': { label: 'Sleeve R · Top', side: 'front', x: 950, y: 800, defaultSize: 100 },
  'sleeve-right-bottom': { label: 'Sleeve R · Cuff', side: 'front', x: 1040, y: 1015, defaultSize: 100 },
  custom: { label: 'Custom (click model)', side: 'front', x: 338, y: 1570, defaultSize: 180 },
};

export const SIZE_PRESETS: { label: string; chestCm: number; lengthCm: number; sleeveCm: number }[] = [
  { label: 'XS', chestCm: 88, lengthCm: 66, sleeveCm: 20 },
  { label: 'S', chestCm: 94, lengthCm: 68, sleeveCm: 21 },
  { label: 'M', chestCm: 100, lengthCm: 70, sleeveCm: 22 },
  { label: 'L', chestCm: 106, lengthCm: 72, sleeveCm: 23 },
  { label: 'XL', chestCm: 112, lengthCm: 74, sleeveCm: 24 },
  { label: '2XL', chestCm: 120, lengthCm: 76, sleeveCm: 25 },
  { label: '3XL', chestCm: 128, lengthCm: 78, sleeveCm: 26 },
];

export const TEXT_LABELS: Record<TextStyleKey, string> = {
  team: 'Team name',
  name: 'Player name',
  number: 'Number',
};

export const TEXT_CONTENT_KEYS: Record<TextStyleKey, keyof Personalisation> = {
  team: 'teamName',
  name: 'playerName',
  number: 'number',
};

/** Texture-space sleeve band (flipY=false): tops ~800, cuffs ~1015 */
const SLEEVE_Y_MIN = 680;
const SLEEVE_Y_MAX = 1050;
/** Mid-axis between measured sleeve centers (L≈324, R≈982) */
const SLEEVE_AXIS = (324 + 982) / 2;

const SLEEVE_MIRROR: Partial<Record<ArtworkPositionId, ArtworkPositionId>> = {
  'sleeve-left': 'sleeve-right',
  'sleeve-right': 'sleeve-left',
  'sleeve-left-top': 'sleeve-right-top',
  'sleeve-right-top': 'sleeve-left-top',
  'sleeve-left-bottom': 'sleeve-right-bottom',
  'sleeve-right-bottom': 'sleeve-left-bottom',
};

function isSleeveY(y: number): boolean {
  return y >= SLEEVE_Y_MIN && y <= SLEEVE_Y_MAX;
}

function mirrorSleeveX(x: number): number {
  return Math.round(2 * SLEEVE_AXIS - x);
}

/** Mirror placement on the opposite sleeve; null if artwork isn’t on a sleeve */
export function mirrorArtworkPlacement(a: Artwork): {
  x: number;
  y: number;
  position: ArtworkPositionId;
  rotation: number;
} | null {
  const mapped = SLEEVE_MIRROR[a.position];
  if (mapped) {
    const anchor = ARTWORK_POSITIONS[mapped];
    return { x: anchor.x, y: anchor.y, position: mapped, rotation: -a.rotation };
  }
  if (a.position === 'custom' && isSleeveY(a.y)) {
    return {
      x: mirrorSleeveX(a.x),
      y: a.y,
      position: 'custom',
      rotation: -a.rotation,
    };
  }
  return null;
}

export type SaveStatus =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved'; mode: 'supabase' | 'local'; shareUrl?: string }
  | { kind: 'error'; message: string };

export type LoadingMode = 'boot' | 'design' | 'product';

export type LoadingState = {
  mode: LoadingMode;
  /** 0–100 */
  progress: number;
  title: string;
  detail: string;
  /** active stage id for checklist */
  stage: string;
  /** optional design/product thumb for preview card */
  thumb?: string;
};

export const LOADING_STAGES: { id: string; label: string }[] = [
  { id: 'hydrate', label: 'Restoring session' },
  { id: 'engine', label: 'Starting 3D engine' },
  { id: 'model', label: 'Loading garment model' },
  { id: 'design', label: 'Painting design texture' },
  { id: 'ready', label: 'Almost ready' },
];

type ConfiguratorState = {
  ready: boolean;
  error: string | null;
  loading: LoadingState | null;
  /** lead captured to unlock the configurator */
  lead: Lead | null;
  leadUnlocked: boolean;
  setLead(lead: Lead): void;
  product: Product;
  products: Product[];
  template: DesignTemplate | null;
  zones: DesignZone[];
  /** zoneId → dataURL art masked to that garment piece */
  zoneArt: Record<string, string>;
  /** multi-region pattern layers (front/back/sleeves/…) */
  designLayers: PatternLayer[];
  /** region currently selected in customize (patterns) tab */
  activePatternRegion: PatternRegionId;
  selectedZoneId: string | null;
  step: WizardStep;
  view: 'front' | 'back';
  panelTab: PanelTab;
  personalisation: Personalisation;
  size: GarmentSize;
  artworks: Artwork[];
  /** selected print object for Adjust tab + canvas pick */
  selectedPrintId: PrintSelection;
  /** when set, next model click places this print at UV */
  placingId: PrintSelection;
  saveStatus: SaveStatus;
  textureFactory: TextureFactory | null;
  view3d: View3D | null;

  init(dom: HTMLElement, textureHost: HTMLElement): Promise<void>;
  setProduct(id: string): Promise<void>;
  setTemplate(id: string): Promise<void>;
  setLoading(patch: Partial<LoadingState> | null): void;
  setZoneColor(zoneId: string, hex: string): void;
  /** mask user image to a zone (null clears); returns false if zone missing */
  setZoneArt(zoneId: string, dataUrl: string | null): boolean;
  /** upsert a pattern layer for a region (null removes it) */
  setPatternLayer(region: PatternRegionId, layer: Omit<PatternLayer, 'id' | 'region'> | null): void;
  updatePatternLayer(id: string, patch: Partial<PatternLayer>): void;
  removePatternLayer(id: string): void;
  setActivePatternRegion(region: PatternRegionId): void;
  /** re-apply layers after template reload */
  reapplyPatternLayers(): void;
  /** rotate camera toward a pattern region (front/back/sleeves) */
  focusPatternRegion(region: PatternRegionId): void;
  selectZone(zoneId: string | null): void;
  setStep(step: WizardStep): void;
  setView(v: 'front' | 'back'): void;
  setPanelTab(tab: PanelTab): void;
  setPersonalisation(p: Partial<Personalisation>): void;
  setTextContent(key: TextStyleKey, content: string): void;
  setTextStyle(key: TextStyleKey, patch: Partial<TextStyle>): void;
  selectPrint(id: PrintSelection): void;
  /** rotate camera to front / back / sleeve for this print */
  focusPrint(id: string): void;
  /** sync selection from canvas UV hit */
  pickPrintAt(uvX: number, uvY: number): boolean;
  beginPlace(id: PrintSelection): void;
  cancelPlace(): void;
  /** place selected print at UV (free placement anywhere on garment) */
  placePrintAt(uvX: number, uvY: number): boolean;
  nudgePrint(dx: number, dy: number): void;
  scalePrint(factor: number): void;
  setSize(p: Partial<GarmentSize>): void;
  applySizePreset(label: string): void;
  placeTeamBadge(): void;
  addArtwork(file: File, position?: ArtworkPositionId): Promise<void>;
  updateArtwork(
    id: string,
    patch: Partial<
      Pick<
        Artwork,
        'position' | 'x' | 'y' | 'w' | 'h' | 'rotation' | 'side' | 'opacity' | 'mirrorSleeves'
      >
    >,
  ): void;
  removeArtwork(id: string): void;
  syncTextureObjects(): void;
  capture(): Promise<void>;
  saveDesign(opts?: { share?: boolean }): Promise<void>;
  shareDesign(): Promise<void>;
  applyDesign(design: SerializedDesign): Promise<void>;
  hydrateFromUrl(): Promise<void>;
  dispose(): void;
};

const first = products[0]!;
let initAttempt = 0;
let hydrated = false;
let initInFlight: Promise<void> | null = null;
/** timestamp when current loading overlay became visible (for min hold) */
let loadingShownAt = 0;

function sleep(ms: number): Promise<void> {
  return new Promise(r => setTimeout(r, ms));
}

async function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: number | undefined;
  try {
    return await Promise.race([
      p,
      new Promise<T>((_, reject) => {
        timer = window.setTimeout(() => reject(new Error(`Timed out: ${label}`)), ms);
      }),
    ]);
  } finally {
    if (timer !== undefined) window.clearTimeout(timer);
  }
}

export const useConfiguratorStore = create<ConfiguratorState>((set, get) => {
  const bootLead = loadLead();
  return {
    ready: false,
    error: null,
    loading: bootLead
      ? {
          mode: 'boot',
          progress: 8,
          title: 'Preparing your studio',
          detail: 'Warming up the 3D workspace…',
          stage: 'hydrate',
        }
      : null,
    lead: bootLead,
    leadUnlocked: !!bootLead,
    setLead(lead) {
      set({ lead, leadUnlocked: true });
      // start boot loader once the gate is lifted
      if (!get().loading && !get().ready && !get().error) {
        loadingShownAt = performance.now();
        set({
          loading: {
            mode: 'boot',
            progress: 8,
            title: 'Preparing your studio',
            detail: 'Warming up the 3D workspace…',
            stage: 'hydrate',
          },
        });
      }
    },
    product: first,
    products,
    template: first.designs.find(d => d.default) ?? first.designs[0] ?? null,
    zones: [],
    zoneArt: {},
    designLayers: [],
    activePatternRegion: 'front',
    selectedZoneId: null,
    step: 0,
    view: 'front',
    panelTab: 'text',
    personalisation: { teamName: 'FELLOW', playerName: 'PLAYER', number: '07' },
    size: { ...DEFAULT_SIZE },
    artworks: [],
    selectedPrintId: 'text:number',
    placingId: null,
    saveStatus: { kind: 'idle' },
    textureFactory: null,
    view3d: null,

    setLoading(patch) {
      if (patch === null) {
        set({ loading: null });
        return;
      }
      const cur = get().loading;
      if (!cur) loadingShownAt = performance.now();
      set({
        loading: {
          mode: 'boot',
          progress: 0,
          title: '',
          detail: '',
          stage: '',
          ...cur,
          ...patch,
        },
      });
    },

    async init(dom, textureHost) {
    const t0 = performance.now();
    const log = (msg: string, extra?: Record<string, unknown> | string | number) => {
      console.info(`[init] +${Math.round(performance.now() - t0)}ms`, msg, extra ?? '');
    };
    const stage = (id: string, progress: number, title: string, detail: string) => {
      if (get().loading?.mode === 'boot') {
        set({
          loading: {
            mode: 'boot',
            progress,
            title,
            detail,
            stage: id,
            thumb: get().template?.thumb,
          },
        });
      }
    };

    // Single-flight: React StrictMode mounts twice; a second concurrent init
    // used to race the first (two WebGL contexts, half-disposed state → stuck spinner).
    if (get().view3d && get().ready) {
      log('skip — already ready');
      get().setLoading(null);
      return;
    }
    if (initInFlight) {
      log('join existing in-flight init');
      return initInFlight;
    }
    // Gate: do not boot 3D until lead unlocks the configurator
    if (!get().leadUnlocked) {
      log('skip — lead gate closed');
      return;
    }

    const attempt = ++initAttempt;
    log('begin', {
      attempt,
      product: get().product.id,
      template: get().template?.id ?? null,
      dom: `${dom.clientWidth}x${dom.clientHeight}`,
      host: `${textureHost.clientWidth}x${textureHost.clientHeight}`,
      canvases: dom.querySelectorAll('canvas').length,
    });
    loadingShownAt = performance.now();
    stage('hydrate', 12, 'Preparing your studio', 'Restoring your last design…');
    let lastStageAt = performance.now();
    const holdStage = async () => {
      const elapsed = performance.now() - lastStageAt;
      if (elapsed < LOADING_MIN_MS) await sleep(LOADING_MIN_MS - elapsed);
      lastStageAt = performance.now();
    };
    // A previous failed/raced init can leave an orphan WebGL canvas in the container.
    if (!get().view3d) {
      for (const c of Array.from(dom.querySelectorAll('canvas'))) c.remove();
    }
    initInFlight = (async () => {
      let view3d: View3D | null = null;
      try {
        if (!hydrated) {
          hydrated = true;
          log('hydrate…');
          await withTimeout(get().hydrateFromUrl(), 8_000, 'hydrate');
          log('hydrate done', { product: get().product.id, zones: get().zones.length });
        } else {
          log('hydrate already done');
        }

        if (attempt !== initAttempt || get().view3d) {
          log('abort after hydrate', { attempt, initAttempt, hasView3d: !!get().view3d });
          return;
        }

        await holdStage();
        stage('engine', 32, 'Starting 3D engine', 'Creating renderer & material pipeline…');
        log('create TextureFactory…');
        const tf = createTextureFactory({
          domContainer: textureHost,
          size: 2000,
          snaplineList: get().product.snaplines,
        });
        log('TextureFactory ok');

        log('createView3D…', { matcap: get().product.matcap });
        view3d = createView3D({
          domContainer: dom,
          texture: tf.getTexture(),
          matcapUrl: get().product.matcap,
        });
        log('createView3D ok', {
          canvas: !!dom.querySelector('canvas'),
          canvasCount: dom.querySelectorAll('canvas').length,
          canvasSize: (() => {
            const c = dom.querySelector('canvas') as HTMLCanvasElement | null;
            return c ? `${c.width}x${c.height}` : null;
          })(),
        });

        tf.signals.on.colorAreaClicked(id => {
          set({ selectedZoneId: id });
        });

        await holdStage();
        stage('model', 52, 'Loading garment', `Fetching ${get().product.name} mesh…`);
        log('loadGLTF…', get().product.glb);
        const model = await withTimeout(loadGLTF(get().product.glb), 45_000, 'loadGLTF');
        log('loadGLTF ok');

        log('setModel…');
        await withTimeout(view3d.setModel(model), 15_000, 'setModel');
        log('setModel ok');

        if (attempt !== initAttempt || get().view3d) {
          log('abort after setModel — disposing', { attempt, initAttempt });
          view3d?.dispose();
          view3d = null;
          return;
        }

        await holdStage();
        stage('design', 74, 'Painting design texture', 'Rasterising SVG zones to GPU…');
        const tpl = get().template;
        if (tpl) {
          log('loadTemplate…', tpl.svg);
          await withTimeout(tf.loadTemplate(tpl.svg), 20_000, 'loadTemplate');
          log('loadTemplate ok', { zones: tf.getZones().length, rasterMs: tf.getRasterMs() });
        } else {
          log('no template — skip loadTemplate');
        }

        if (attempt !== initAttempt || get().view3d) {
          log('abort after template — disposing', { attempt, initAttempt });
          view3d?.dispose();
          view3d = null;
          return;
        }

        const pending = get().zones;
        if (pending.length) {
          log('restore pending colors', pending.length);
          for (const z of pending) tf.setTemplateColor({ id: z.id, hex: z.hex });
        }

        get().syncTextureObjects();

        await holdStage();
        stage('ready', 96, 'Finishing touches', 'Syncing prints & materials…');
        if (!view3d) throw new Error('createView3D returned no renderer');
        view3d.playIntroSpin();
        const zones = tf.getZones();
        // Keep final ready state readable for at least LOADING_MIN_MS
        await sleep(LOADING_MIN_MS);
        set({
          textureFactory: tf,
          view3d,
          ready: true,
          error: null,
          zones,
          loading: null,
        });
        log('READY', { zones: zones.length, rasterMs: tf.getRasterMs(), canvases: dom.querySelectorAll('canvas').length });
        set({ zoneArt: tf.getZoneArt() });
        tf.setPatternLayers(get().designLayers);
      } catch (e) {
        const stale = attempt !== initAttempt || !!get().view3d;
        log('FAILED', {
          stale,
          attempt,
          initAttempt,
          hasView3d: !!get().view3d,
          message: e instanceof Error ? e.message : String(e),
        });
        console.error('[init] stack', e);
        // Always tear down a half-built renderer so it cannot stack under the next attempt
        if (!get().view3d) {
          view3d?.dispose();
          view3d = null;
          for (const c of Array.from(dom.querySelectorAll('canvas'))) c.remove();
        }
        if (stale) return;
        set({ error: e instanceof Error ? e.message : String(e), ready: false, loading: null });
      } finally {
        initInFlight = null;
        log('initInFlight cleared', { canvases: dom.querySelectorAll('canvas').length });
      }
    })();

    return initInFlight;
  },

  async setProduct(id) {
    const product = products.find(p => p.id === id);
    if (!product) return;
    const template = product.designs.find(d => d.default) ?? product.designs[0] ?? null;
    if (!loadingShownAt) loadingShownAt = performance.now();
    set({
      product,
      template,
      zones: [],
      zoneArt: {},
      designLayers: [],
      selectedZoneId: null,
      ready: false,
      loading: {
        mode: 'product',
        progress: 20,
        title: 'Switching garment',
        detail: `Loading ${product.name}…`,
        stage: 'model',
        thumb: template?.thumb,
      },
    });

    const { view3d, textureFactory } = get();
    if (!view3d || !textureFactory) {
      set({ loading: null, error: '3D engine not ready — try again in a moment.' });
      return;
    }
    try {
      get().setLoading({ progress: 45, detail: 'Fetching 3D mesh…', stage: 'model' });
      const model = await loadGLTF(product.glb);
      await view3d.setModel(model);
      get().setLoading({ progress: 75, detail: 'Applying base design…', stage: 'design' });
      if (template) await textureFactory.loadTemplate(template.svg);
      const held = LOADING_MIN_MS - (performance.now() - loadingShownAt);
      if (held > 0) await sleep(held);
      set({ zones: textureFactory.getZones(), zoneArt: textureFactory.getZoneArt(), ready: true, error: null, loading: null });
      view3d.playIntroSpin();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e), ready: true, loading: null });
    }
  },

  async setTemplate(id) {
    const tpl = get().product.designs.find(d => d.id === id);
    const tf = get().textureFactory;
    if (!tpl || !tf) {
      if (tpl) {
        set({ template: tpl, loading: null });
      }
      return;
    }
    // Keep the previous texture visible; show a light design-swap overlay
    loadingShownAt = performance.now();
    set({
      template: tpl,
      loading: {
        mode: 'design',
        progress: 18,
        title: 'Applying design',
        detail: tpl.name,
        stage: 'design',
        thumb: tpl.thumb,
      },
    });
    try {
      get().setLoading({ progress: 42, detail: 'Parsing SVG zones…' });
      await tf.loadTemplate(tpl.svg);
      get().setLoading({ progress: 72, detail: 'Restoring colours & layers…' });
      // restore colors if same zone ids still exist
      const zones = tf.getZones();
      const prev = get().zones;
      for (const z of zones) {
        const hit = prev.find(p => p.id === z.id);
        if (hit) tf.setTemplateColor({ id: z.id, hex: hit.hex });
      }
      // re-mask zone art for zones that survived the template swap
      const prevArt = get().zoneArt;
      const nextArt: Record<string, string> = {};
      for (const z of zones) {
        const art = prevArt[z.id];
        if (art) {
          tf.setZoneArt(z.id, art);
          nextArt[z.id] = art;
        }
      }
      get().setLoading({ progress: 92, detail: 'Syncing pattern layers…' });
      set({ zones: tf.getZones(), zoneArt: nextArt, ready: true, selectedZoneId: null });
      // pattern layers use UV rects / zone paths — re-apply on the new template
      tf.setPatternLayers(get().designLayers);
      get().setLoading({ progress: 100, title: 'Design applied', detail: tpl.name, stage: 'ready' });
      // readable hold (at least 1s from first paint)
      const held = LOADING_MIN_MS - (performance.now() - loadingShownAt);
      if (held > 0) await sleep(held);
      set({ loading: null });
    } catch (e) {
      set({ loading: null, error: e instanceof Error ? e.message : String(e) });
    }
  },

  setZoneColor(zoneId, hex) {
    const tf = get().textureFactory;
    if (!tf) return;
    tf.setTemplateColor({ id: zoneId, hex });
    set({ zones: tf.getZones() });
  },

  setZoneArt(zoneId, dataUrl) {
    const tf = get().textureFactory;
    if (!tf) return false;
    tf.setZoneArt(zoneId, dataUrl);
    const zoneArt = { ...get().zoneArt };
    if (dataUrl) zoneArt[zoneId] = dataUrl;
    else delete zoneArt[zoneId];
    set({ zoneArt });
    return true;
  },

  setPatternLayer(region, layer) {
    const tf = get().textureFactory;
    const prev = get().designLayers.filter(l => l.region !== region);
    if (!layer) {
      set({ designLayers: prev });
      tf?.setPatternLayers(prev);
      return;
    }
    const existing = get().designLayers.find(l => l.region === region);
    // New src = new artwork → colour overrides only kept if provided on this call
    const next: PatternLayer = {
      id: existing?.id ?? `pl-${crypto.randomUUID()}`,
      region,
      ...layer,
      colorOverrides:
        layer.colorOverrides !== undefined
          ? layer.colorOverrides
          : existing && existing.src === layer.src
            ? existing.colorOverrides
            : undefined,
    };
    const designLayers = [...prev, next];
    set({ designLayers, activePatternRegion: region });
    tf?.setPatternLayers(designLayers);
  },

  updatePatternLayer(id, patch) {
    const designLayers = get().designLayers.map(l => (l.id === id ? { ...l, ...patch, id: l.id, region: patch.region ?? l.region } : l));
    set({ designLayers });
    get().textureFactory?.setPatternLayers(designLayers);
  },

  removePatternLayer(id) {
    const designLayers = get().designLayers.filter(l => l.id !== id);
    set({ designLayers });
    get().textureFactory?.setPatternLayers(designLayers);
  },

  setActivePatternRegion: region => set({ activePatternRegion: region }),

  reapplyPatternLayers() {
    const tf = get().textureFactory;
    if (!tf) return;
    tf.setPatternLayers(get().designLayers);
  },

  focusPatternRegion(region) {
    const view3d = get().view3d;
    if (!view3d) return;
    if (region === 'back') get().setView('back');
    else if (region === 'sleeve-left') {
      view3d.smoothRotateTo(-55);
      set({ view: 'front' });
    } else if (region === 'sleeve-right') {
      view3d.smoothRotateTo(55);
      set({ view: 'front' });
    } else if (region === 'front' || region.startsWith('zone:') || region === 'collar' || region === 'cuff' || region === 'hem' || region === 'placket') {
      get().setView('front');
    }
  },

  selectZone: zoneId => set({ selectedZoneId: zoneId }),
  setStep: step => set({ step }),
  setView: v => {
    const view3d = get().view3d;
    if (!view3d) return;
    if (v === 'front') view3d.setFrontView();
    else view3d.setBackView();
    set({ view: v });
  },

  setPersonalisation: p => {
    set({ personalisation: { ...get().personalisation, ...p } });
    get().syncTextureObjects();
  },

  setPanelTab: tab => set({ panelTab: tab }),

  setTextContent(key, content) {
    const field = TEXT_CONTENT_KEYS[key];
    const p = { ...get().personalisation };
    (p as Record<string, unknown>)[field] = content;
    set({ personalisation: p });
    get().syncTextureObjects();
  },

  setTextStyle(key, patch) {
    const p = { ...get().personalisation };
    const styleKey = key === 'team' ? 'teamStyle' : key === 'name' ? 'nameStyle' : 'numberStyle';
    const current = resolveTextStyle(key, p);
    p[styleKey] = { ...current, ...patch };
    set({ personalisation: p });
    get().syncTextureObjects();
  },

  selectPrint(id) {
    const prev = get().selectedPrintId;
    set({ selectedPrintId: id, placingId: null });
    if (id) set({ panelTab: 'size' });
    if (prev !== id) get().syncTextureObjects();
    if (id) get().focusPrint(id);
  },

  focusPrint(id) {
    const view3d = get().view3d;
    if (!id || !view3d) return;

    if (isTextSelection(id)) {
      const key = id.slice(5) as TextStyleKey;
      const s = resolveTextStyle(key, get().personalisation);
      if (s.y > 1200 || s.y < 400) get().setView('back');
      else get().setView('front');
      return;
    }

    const art = get().artworks.find(a => a.id === id);
    if (!art) return;

    const onSleeve =
      art.position.startsWith('sleeve') ||
      (art.position === 'custom' && art.y >= SLEEVE_Y_MIN && art.y <= SLEEVE_Y_MAX);

    if (onSleeve) {
      const left =
        art.position.startsWith('sleeve-left') ||
        (art.position === 'custom' && art.x < SLEEVE_AXIS);
      const deg = left ? -55 : 55;
      view3d.smoothRotateTo(deg);
      set({ view: 'front' });
      console.info('[focus] sleeve', { id, deg, left });
      return;
    }

    get().setView(art.side === 'back' ? 'back' : 'front');
  },

  pickPrintAt(uvX, uvY) {
    if (get().placingId) return get().placePrintAt(uvX, uvY);

    const tf = get().textureFactory;
    if (!tf) return false;
    const hit = tf.pickObject(uvX, uvY);
    if (!hit) return false;

    let id: string;
    if (hit.role && TEXT_ROLES.has(hit.role)) {
      id = `text:${hit.role}`;
    } else if (hit.role && get().artworks.some(a => a.id === hit.role)) {
      id = hit.role;
    } else if (hit.uuid.startsWith('I-')) {
      const match = get().artworks.find(a => a.id === hit.uuid.slice(2));
      id = match?.id ?? hit.uuid;
      if (id.startsWith('I-')) return false;
    } else {
      return false;
    }

    const prev = get().selectedPrintId;
    set({ selectedPrintId: id, panelTab: 'size', placingId: null });
    if (prev !== id) get().syncTextureObjects();
    get().focusPrint(id);
    console.info('[pick] print', id);
    return true;
  },

  beginPlace(id) {
    if (!id) return;
    set({ placingId: id, selectedPrintId: id, panelTab: 'size' });
    if (id.startsWith('text:')) {
      const key = id.slice(5) as TextStyleKey;
      const s = resolveTextStyle(key, get().personalisation);
      get().setView(s.y > 1200 ? 'back' : 'front');
    } else {
      const art = get().artworks.find(a => a.id === id);
      if (art) get().setView(art.side);
    }
    console.info('[place] arm', id);
  },

  cancelPlace() {
    set({ placingId: null });
  },

  placePrintAt(uvX, uvY) {
    const id = get().placingId;
    if (!id) return false;
    const size = 2000;
    const x = Math.round(uvX * size);
    const y = Math.round(uvY * size);
    // Side from current camera view (user must be looking at the face they click)
    const side: ArtworkSide = get().view === 'back' ? 'back' : 'front';

    if (isTextSelection(id)) {
      const key = id.slice(5) as TextStyleKey;
      get().setTextStyle(key, { x, y });
    } else {
      get().updateArtwork(id, { x, y, side, position: 'custom' });
    }
    set({ placingId: null, selectedPrintId: id, panelTab: 'size' });
    console.info('[place] done', { id, x, y, side });
    return true;
  },

  nudgePrint(dx, dy) {
    const id = get().selectedPrintId;
    if (!id) return;
    if (isTextSelection(id)) {
      const key = id.slice(5) as TextStyleKey;
      const style = resolveTextStyle(key, get().personalisation);
      get().setTextStyle(key, { x: style.x + dx, y: style.y + dy });
    } else {
      const art = get().artworks.find(a => a.id === id);
      if (art) get().updateArtwork(id, { x: art.x + dx, y: art.y + dy, position: 'custom' });
    }
  },

  scalePrint(factor) {
    const id = get().selectedPrintId;
    if (!id) return;
    if (isTextSelection(id)) {
      const key = id.slice(5) as TextStyleKey;
      const style = resolveTextStyle(key, get().personalisation);
      const next = Math.max(12, Math.round(style.fontSize * factor));
      get().setTextStyle(key, { fontSize: next });
    } else {
      const art = get().artworks.find(a => a.id === id);
      if (!art) return;
      const w = Math.max(20, Math.round(art.w * factor));
      const h = Math.max(20, Math.round(art.h * factor));
      get().updateArtwork(id, { w, h });
    }
  },

  setSize: p => {
    set({ size: { ...get().size, ...p } });
  },

  applySizePreset(label) {
    const preset = SIZE_PRESETS.find(s => s.label === label);
    if (!preset) return;
    set({ size: { ...preset } });
    console.info('[size] preset', preset);
  },

  syncTextureObjects() {
    const tf = get().textureFactory;
    if (!tf) return;
    const { personalisation, artworks } = get();
    tf.clearObjects();

    const drawTextItem = (key: TextStyleKey, content: string) => {
      if (!content.trim()) return;
      const s = resolveTextStyle(key, personalisation);
      tf.addText(content.trim(), s.x, s.y, {
        fontSize: String(s.fontSize),
        fill: s.fill,
        stroke: s.stroke,
        strokeWidth: s.strokeWidth,
        rotation: s.rotation,
        visible: s.visible,
        role: key,
      });
    };

    drawTextItem('team', personalisation.teamName);
    drawTextItem('name', personalisation.playerName);
    drawTextItem('number', personalisation.number);

    for (const a of artworks) {
      tf.addImage(a.dataUrl, a.x, a.y, a.w, a.h, a.rotation, a.opacity ?? 1, a.id);
      if (a.mirrorSleeves) {
        const m = mirrorArtworkPlacement(a);
        if (m) {
          tf.addImage(a.dataUrl, m.x, m.y, a.w, a.h, m.rotation, a.opacity ?? 1, a.id);
        }
      }
      // Selection highlight on mockup
      const sel = get().selectedPrintId;
      if (sel === a.id) {
        tf.addHighlight(a.x, a.y, a.w, a.h, a.rotation);
        if (a.mirrorSleeves) {
          const m = mirrorArtworkPlacement(a);
          if (m) tf.addHighlight(m.x, m.y, a.w, a.h, m.rotation);
        }
      }
    }

    // Text selection highlight
    const selId = get().selectedPrintId;
    if (isTextSelection(selId)) {
      const key = selId.slice(5) as TextStyleKey;
      const s = resolveTextStyle(key, personalisation);
      const content =
        key === 'team'
          ? personalisation.teamName
          : key === 'name'
            ? personalisation.playerName
            : personalisation.number;
      if (content?.trim() && s.visible) {
        const approxW = Math.max(content.trim().length * s.fontSize * 0.55, s.fontSize);
        const approxH = s.fontSize * 1.1;
        tf.addHighlight(s.x, s.y, approxW, approxH, s.rotation);
      }
    }

    tf.scheduleRaster();
  },

  placeTeamBadge() {
    get().syncTextureObjects();
    get().setView('back');
  },

  async addArtwork(file, position = 'front-center') {
    if (!file.type.startsWith('image/')) {
      console.warn('[artwork] not an image', file.type);
      return;
    }
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error ?? new Error('read failed'));
      reader.readAsDataURL(file);
    });

    const anchor = ARTWORK_POSITIONS[position];
    const base = anchor.defaultSize;
    // keep aspect from natural image
    const dims = await new Promise<{ w: number; h: number }>(resolve => {
      const img = new Image();
      img.onload = () => {
        const scale = base / Math.max(img.naturalWidth || base, img.naturalHeight || base, 1);
        resolve({
          w: Math.round((img.naturalWidth || base) * scale),
          h: Math.round((img.naturalHeight || base) * scale),
        });
      };
      img.onerror = () => resolve({ w: base, h: base });
      img.src = dataUrl;
    });

    const art: Artwork = {
      id: crypto.randomUUID(),
      name: file.name.replace(/\.[^.]+$/, '') || 'Artwork',
      dataUrl,
      side: anchor.side,
      position,
      x: anchor.x,
      y: anchor.y,
      w: dims.w,
      h: dims.h,
      rotation: 0,
      opacity: 1,
      mirrorSleeves: false,
    };
    set({ artworks: [...get().artworks, art], panelTab: 'size', selectedPrintId: art.id });
    get().syncTextureObjects();
    if (anchor.side === 'back') get().setView('back');
    else get().setView('front');
    // Offer free placement immediately
    get().beginPlace(art.id);
    console.info('[artwork] added', { id: art.id, position, w: art.w, h: art.h });
  },

  updateArtwork(id, patch) {
    const list = get().artworks;
    const idx = list.findIndex(a => a.id === id);
    if (idx < 0) return;
    const prev = list[idx];
    let next: Artwork = { ...prev, ...patch };
    if (patch.position && patch.position !== 'custom') {
      const anchor = ARTWORK_POSITIONS[patch.position];
      next = {
        ...next,
        side: anchor.side,
        x: anchor.x,
        y: anchor.y,
      };
      if (anchor.side === 'back') get().setView('back');
      else get().setView('front');
      // Rotate camera to sleeve angles / front-back after preset jump
      queueMicrotask(() => get().focusPrint(id));
    }
    // free x/y always updates side from current view when custom
    if ((patch.x !== undefined || patch.y !== undefined) && (!patch.position || patch.position === 'custom')) {
      next = { ...next, position: patch.position ?? next.position };
    }
    const artworks = list.slice();
    artworks[idx] = next;
    set({ artworks });
    get().syncTextureObjects();
  },

  removeArtwork(id) {
    set({
      artworks: get().artworks.filter(a => a.id !== id),
      selectedPrintId: get().selectedPrintId === id ? null : get().selectedPrintId,
    });
    get().syncTextureObjects();
  },

  async capture() {
    const view3d = get().view3d;
    if (!view3d) return;
    // Strip selection chrome so exports are clean
    const prevSel = get().selectedPrintId;
    if (prevSel) {
      set({ selectedPrintId: null });
      get().syncTextureObjects();
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
    }
    const images = await view3d.captureFrontBack();
    if (prevSel) {
      set({ selectedPrintId: prevSel });
      get().syncTextureObjects();
    }
    if (!images) return;
    downloadDataUrl(images.frontImage, 'design-front.png');
    downloadDataUrl(images.backImage, 'design-back.png');
  },

  async saveDesign(opts = {}) {
    const s = get();
    set({ saveStatus: { kind: 'saving' } });
    try {
      const design = serializeDesign({
        productId: s.product.id,
        templateId: s.template?.id ?? null,
        zones: s.zones,
        personalisation: s.personalisation,
        size: s.size,
        artworks: s.artworks,
        layers: s.designLayers,
      });
      saveLocalDesign(design);

      const result = await saveDesignToCloud(design, { isPublic: opts.share === true });
      const shareUrl =
        result.shareSlug && supabaseEnabled
          ? `${window.location.origin}${window.location.pathname}?share=${result.shareSlug}`
          : undefined;

      set({
        saveStatus: {
          kind: 'saved',
          mode: result.mode,
          shareUrl,
        },
      });
    } catch (e) {
      set({
        saveStatus: {
          kind: 'error',
          message: e instanceof Error ? e.message : String(e),
        },
      });
    }
  },

  async shareDesign() {
    const s = get();
    set({ saveStatus: { kind: 'saving' } });
    try {
      const design = serializeDesign({
        productId: s.product.id,
        templateId: s.template?.id ?? null,
        zones: s.zones,
        personalisation: s.personalisation,
        size: s.size,
        artworks: s.artworks,
        layers: s.designLayers,
      });
      saveLocalDesign(design);

      const result = await saveDesignToCloud(design, { isPublic: true });
      let shareUrl: string;
      if (result.shareSlug) {
        shareUrl = `${window.location.origin}${window.location.pathname}?share=${result.shareSlug}`;
      } else {
        // offline / local fallback: encode full state in hash
        shareUrl = `${window.location.origin}${window.location.pathname}#d=${encodeShareHash(design)}`;
      }

      try {
        await navigator.clipboard.writeText(shareUrl);
      } catch {
        /* clipboard may be blocked */
      }

      set({ saveStatus: { kind: 'saved', mode: result.mode, shareUrl } });
      // also put in address bar for manual copy
      window.history.replaceState(null, '', `#d=${encodeShareHash(design)}`);
    } catch (e) {
      set({
        saveStatus: {
          kind: 'error',
          message: e instanceof Error ? e.message : String(e),
        },
      });
    }
  },

  async applyDesign(design) {
    const product = products.find(p => p.id === design.productId);
    if (!product) return;

    set({
      product,
      personalisation: design.personalisation,
      size: design.size ?? { ...DEFAULT_SIZE },
      artworks: design.artworks ?? [],
      designLayers: design.layers ?? [],
      zones: design.colors,
      selectedZoneId: null,
      ready: false,
      loading: {
        mode: 'product',
        progress: 25,
        title: 'Loading saved design',
        detail: product.name,
        stage: 'model',
        thumb: product.designs.find(d => d.id === design.templateId)?.thumb,
      },
    });
    loadingShownAt = performance.now();

    const template =
      product.designs.find(d => d.id === design.templateId) ??
      product.designs.find(d => d.default) ??
      product.designs[0] ??
      null;
    set({ template });

    const { view3d, textureFactory } = get();
    if (!view3d || !textureFactory) {
      set({ loading: null, error: '3D engine not ready — try again in a moment.' });
      return;
    }

    try {
      const model = await loadGLTF(product.glb);
      await view3d.setModel(model);
      get().setLoading({ progress: 70, detail: 'Painting design texture…', stage: 'design' });
      if (template) await textureFactory.loadTemplate(template.svg);
      for (const z of design.colors) {
        textureFactory.setTemplateColor({ id: z.id, hex: z.hex });
      }
      textureFactory.setPatternLayers(get().designLayers);
      const held = LOADING_MIN_MS - (performance.now() - loadingShownAt);
      if (held > 0) await sleep(held);
      set({ zones: textureFactory.getZones(), zoneArt: textureFactory.getZoneArt(), ready: true, error: null, loading: null });
      get().syncTextureObjects();
      view3d.playIntroSpin();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e), ready: true, loading: null });
    }
  },

  async hydrateFromUrl() {
    try {
      console.info('[hydrate] begin', window.location.search + window.location.hash);
      const params = new URLSearchParams(window.location.search);
      const shareSlug = params.get('share');
      if (shareSlug) {
        console.info('[hydrate] share slug', shareSlug);
        const remote = await loadDesignBySlug(shareSlug);
        if (remote) {
          // apply product/colors into state; TF applies after init if present
          const product = products.find(p => p.id === remote.productId);
          if (product) {
            set({
              product,
              template:
                product.designs.find(d => d.id === remote.templateId) ??
                product.designs.find(d => d.default) ??
                product.designs[0] ??
                null,
              personalisation: remote.personalisation,
              size: remote.size ?? { ...DEFAULT_SIZE },
              artworks: remote.artworks ?? [],
              zones: remote.colors,
            });
          }
          return;
        }
      }

      const hash = window.location.hash.replace(/^#/, '');
      if (hash.startsWith('d=')) {
        const decoded = decodeShareHash(hash.slice(2));
        if (decoded) {
          const product = products.find(p => p.id === decoded.productId);
          if (product) {
            set({
              product,
              template:
                product.designs.find(d => d.id === decoded.templateId) ??
                product.designs.find(d => d.default) ??
                product.designs[0] ??
                null,
              personalisation: decoded.personalisation,
              size: decoded.size ?? { ...DEFAULT_SIZE },
              artworks: decoded.artworks ?? [],
              zones: decoded.colors,
            });
          }
          return;
        }
      }

      const local = loadLocalDesign();
      if (local) {
        const product = products.find(p => p.id === local.productId);
        if (product) {
          set({
            product,
            template:
              product.designs.find(d => d.id === local.templateId) ??
              product.designs.find(d => d.default) ??
              product.designs[0] ??
              null,
            personalisation: local.personalisation,
            size: local.size ?? { ...DEFAULT_SIZE },
            artworks: local.artworks ?? [],
            zones: local.colors,
          });
        }
      }
    } catch (e) {
      console.warn('[hydrate]', e);
    }
  },

  dispose() {
    initAttempt += 1;
    get().view3d?.dispose();
    set({ view3d: null, textureFactory: null, ready: false });
  },
  };
});

function downloadDataUrl(dataUrl: string, filename: string) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  a.click();
}
